import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { participantSurveyService } from '../services/api/participantSurveyService'
import {
  SurveyResponse,
  DemographicResponse,
  ValidityResponse,
  SurveyStep,
  SurveyQuestion,
  ProgressInfo
} from '../types/survey'

// Import ExtendedSurveyParticipant from survey types
import { ExtendedSurveyParticipant } from '../types/survey'

/**
 * Survey Session Interface
 * Represents the complete state of a survey session
 * This is the main data structure that gets persisted
 */
export interface SurveySession {
  accessCode: string
  participant: ExtendedSurveyParticipant
  currentStep: SurveyStep
  currentQuestionId: string | null
  responses: Record<string, SurveyResponse> // Regular survey responses
  demographics: Record<string, DemographicResponse> // Demographic answers
  validityResponses: Record<string, ValidityResponse> // Validity question answers
  surveyStartedAt: number
  lastActivityAt: number
  language: string
  isCompleted: boolean
  currentQuestionStartTime?: number // Timestamp when current question was displayed
}

/**
 * Participant Survey State Interface
 * Defines the complete Zustand store state and actions
 */
interface ParticipantSurveyState {
  currentSession: SurveySession | null
  isLoading: boolean
  error: string | null

  // Authentication and session management
  authenticateParticipant: (accessCode: string) => Promise<void>
  resumeSession: (accessCode: string) => Promise<boolean>
  
  // Response management
  saveResponse: (questionId: string, response: SurveyResponse) => void
  saveDemographicResponse: (questionId: string, response: DemographicResponse) => void
  saveValidityResponse: (questionId: string, response: ValidityResponse) => void
  
  // Navigation and state management
  setCurrentStep: (step: SurveyStep) => void
  setCurrentQuestion: (questionId: string | null) => void
  setLanguage: (language: string) => void
  
  // Survey completion
  submitSurvey: () => Promise<void>
  
  // Utility actions
  pushSecondaryIndicatorResponses: () => void
  removeSecondaryIndicatorResponses: (indicatorId: string) => void
  clearSession: () => void
  resetError: () => void
  setLoading: (loading: boolean) => void
  
  // Computed values and utilities
  getCurrentQuestion: () => SurveyQuestion | null
  getProgress: () => ProgressInfo
  getAllQuestions: () => SurveyQuestion[]
  getResponsesCount: () => number
  isQuestionAnswered: (questionId: string) => boolean
  canProceed: () => boolean
}

const initialState = {
  currentSession: null,
  isLoading: false,
  error: null,
}

export const useParticipantSurveyStore = create<ParticipantSurveyState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        /**
         * Authenticate participant with access code
         * This is the entry point for starting a new survey session
         */
        authenticateParticipant: async (accessCode: string) => {
          set({ isLoading: true, error: null })
          
          try {
            const participant = await participantSurveyService.authenticateParticipant(accessCode)
            
            console.log('Original participant data:', {
              indicatorsCount: participant.indicators?.length || 0,
              validityQuestionsCount: participant.validityQuestions?.length || 0,
              validityQuestions: participant.validityQuestions
            })
            
            // Check if we're resuming an existing session
            const existingSession = get().currentSession
            const isResuming = existingSession?.accessCode === accessCode && !existingSession.isCompleted
            
            if (isResuming) {
              // Resume existing session - just update last activity
            set({ 
                isLoading: false,
                currentSession: {
                  ...existingSession,
                  lastActivityAt: Date.now()
                }
              })
            } else {
              // Distribute validity questions for elementary/uppergrade surveys only
              const isElementaryOrUppergrade = participant.appliedTo === 'elementary' || 
                                               participant.appliedTo === 'highschool' || 
                                               participant.appliedTo === 'uppergrades'
              
              if (isElementaryOrUppergrade && participant.validityQuestions?.length) {
                console.log('Distributing validity questions for elementary/uppergrade survey')
                
                let validityIndex = 0
                const validityQuestions = participant.validityQuestions
                
                // Add one validity question to the end of each eligible indicator
                participant.indicators?.forEach((indicator, index) => {
                  if (validityIndex < validityQuestions.length) {
                    // Skip only K3 indicators (allow trauma/input_rule_event indicators)
                    const isK3 = indicator.appliesTo?.includes('k3')
                    
                    if (!isK3) {
                      const validityQuestion = validityQuestions[validityIndex]
                      
                      indicator.questions = indicator.questions || []
                      indicator.questions.push({
                        ...validityQuestion,
                        ...(validityQuestion.validityIndex !== undefined && { validityIndex: validityQuestion.validityIndex }),
                        isValidityQuestion: true
                      })
                      
                      console.log(`Added validity question to indicator ${index} (${indicator.indicator})`)
                      validityIndex++
                    }
                  }
                })
              }
              
              // DEBUG: Check if the problematic validity question appears in multiple indicators
              console.log('Final validity question distribution check:')
              let problematicQuestionCount = 0
              participant.indicators?.forEach((indicator, idx) => {
                const problematicQuestion = indicator.questions?.find(q => q.questionId === '5dd156f5-d424cc6c-673b32bc-55a7e503')
                if (problematicQuestion) {
                  problematicQuestionCount++
                  console.log(`Problematic validity question found in indicator ${idx} (${indicator.indicator}):`, {
                    questionId: problematicQuestion.questionId,
                    isValidityQuestion: problematicQuestion.isValidityQuestion,
                    validityIndex: problematicQuestion.validityIndex,
                    indicatorName: indicator.indicator,
                    indicatorId: indicator.indicatorId
                  })
                }
              })
              
              if (problematicQuestionCount > 1) {
                console.error(`CRITICAL: Problematic validity question appears ${problematicQuestionCount} times across indicators! This will cause navigation loops.`)
              } else if (problematicQuestionCount === 1) {
                console.log('Problematic validity question appears exactly once - this is correct')
              } else {
                console.log('Problematic validity question not found in this survey')
              }
              
              // Create the extended participant with computed values
              const extendedParticipant: ExtendedSurveyParticipant = {
                ...participant,
                indicatorsCount: participant.indicators?.length || 0,
                validityQuestionsCount: participant.validityQuestions?.length || 0,
                schoolName: participant.schoolName || '',
                isAllCore: participant.isAllCore || false,
                demographicQuestions: participant.demographicQuestions || [],
                validityQuestions: participant.validityQuestions || [],
                indicators: participant.indicators || [],
                responseOptionsIndex: participant.responseOptionsIndex || {},
                appliedTo: participant.appliedTo || '',
                introK3: participant.introK3 || [],
                wellnessCenterCheck: participant.wellnessCenterCheck || false
              }

              // Create initial session
              const session: SurveySession = {
                accessCode, 
                participant: extendedParticipant,
                currentStep: 'intro',
                currentQuestionId: null,
                responses: {},
                demographics: {},
                validityResponses: {},
                surveyStartedAt: Date.now(),
                lastActivityAt: Date.now(),
                language: 'en',
                isCompleted: false
              }
              
              set({
                currentSession: session,
              isLoading: false,
                error: null
              })
            }
          } catch (error) {
            console.log('[participant-survey-store][authenticateParticipant] Error:', error)
            set({ 
              error: (error as Error).message, 
              isLoading: false 
            })
            throw error
          }
        },

        /**
         * Resume an existing survey session
         * Used when a participant returns with the same access code
         */
        resumeSession: async (accessCode: string) => {
          const { currentSession } = get()
          if (!currentSession || currentSession.accessCode !== accessCode) {
            return false
          }
          
          // Update last activity and return success
          set({
            currentSession: {
              ...currentSession,
              lastActivityAt: Date.now()
            }
          })
          return true
        },

        /**
         * Save a survey response
         * Handles both regular questions and validity questions
         */
        saveResponse: (questionId: string, response: SurveyResponse) => {
          const { currentSession } = get()
          if (!currentSession) return
          
          // Check if this is a validity question or introK3 question
          const currentQuestion = get().getCurrentQuestion()
          const isValidityQuestion = currentQuestion?.isValidityQuestion || response.validityIndex !== undefined
          const isIntroK3Question = currentQuestion?.indicatorId === 'introK3'
          
          // Calculate time spent and determine if this is initial or revision
          let initialTimeSpentMs: number | undefined
          let revisionTimeSpentMs: number | undefined
          let isRevision = false
          
          if (currentSession.currentQuestionStartTime && !isIntroK3Question) {
            const currentTimeSpent = Date.now() - currentSession.currentQuestionStartTime
            
            // Check if this question has been answered before
            const existingResponse = currentSession.responses[questionId] || currentSession.validityResponses[questionId]
            isRevision = !!existingResponse
            
            if (isRevision) {
              // For revisions, add the current time spent to existing revision time
              // This handles both same-question changes and cross-question navigation
              const existingRevisionTime = existingResponse?.revisionTimeSpentMs || 0
              revisionTimeSpentMs = existingRevisionTime + currentTimeSpent
              console.log(`Time tracking (revision): Question ${questionId} session time ${currentTimeSpent}ms, existing revision time ${existingRevisionTime}ms, total revision time ${revisionTimeSpentMs}ms`)
            } else {
              initialTimeSpentMs = currentTimeSpent
              console.log(`Time tracking (initial): Question ${questionId} took ${initialTimeSpentMs}ms (${Math.round(initialTimeSpentMs / 1000)}s)`)
            }
          } else if (isIntroK3Question) {
            console.log(`Time tracking skipped for introK3 question: ${questionId}`)
          }
          
          console.log('saveResponse: Saving response', {
            questionId,
            response,
            currentQuestion,
            isValidityQuestion,
            isIntroK3: isIntroK3Question,
            isRevision,
            initialTimeSpentMs,
            revisionTimeSpentMs
          });
          
          if (isValidityQuestion) {
            // Save to validity responses - these are tracked separately for data quality
            console.log('saveResponse: Saving as validity response');
            
            // Include time tracking for validity questions in elementary/uppergrade surveys
            const isElementaryOrUppergrade = currentSession.participant.appliedTo === 'elementary' || 
                                           currentSession.participant.appliedTo === 'highschool' || 
                                           currentSession.participant.appliedTo === 'uppergrades'
            
            // Build the validity response object, conditionally including time tracking
            const validityResponseToSave: ValidityResponse = {
              questionId: response.questionId,
              ...(response.validityIndex !== undefined && { validityIndex: response.validityIndex }),
              responseOptionField: response.responseOptionField,
              responseScore: response.responseScore,
              ...(isElementaryOrUppergrade && !isIntroK3Question && isRevision && {
                initialTimeSpentMs: currentSession.validityResponses[questionId]?.initialTimeSpentMs,
                revisionTimeSpentMs: revisionTimeSpentMs
              }),
              ...(isElementaryOrUppergrade && !isIntroK3Question && !isRevision && {
                initialTimeSpentMs: initialTimeSpentMs
              })
            }
            
            set({
              currentSession: {
                ...currentSession,
                validityResponses: {
                  ...currentSession.validityResponses,
                  [questionId]: validityResponseToSave
                },
                lastActivityAt: Date.now()
              }
            })
          } else {
            // Save to regular responses - these are the main survey data
            console.log('saveResponse: Saving as regular response');
            
            // Build the response object, conditionally including time tracking
            let responseToSave: SurveyResponse
            
            if (isIntroK3Question) {
              // Don't include time tracking for introK3
              responseToSave = { ...response }
            } else {
              // Include time tracking for regular questions
              responseToSave = { ...response }
              
              if (isRevision) {
                // For revisions, preserve existing initial time and add revision time
                const existingResponse = currentSession.responses[questionId]
                responseToSave.initialTimeSpentMs = existingResponse?.initialTimeSpentMs
                responseToSave.revisionTimeSpentMs = revisionTimeSpentMs
              } else {
                // For initial responses, set initial time
                responseToSave.initialTimeSpentMs = initialTimeSpentMs
              }
            }
            
            set({
              currentSession: {
                ...currentSession,
                responses: {
                  ...currentSession.responses,
                  [questionId]: responseToSave
                },
                lastActivityAt: Date.now()
              }
            })
          }

          // Check for input_rule_event questions that should trigger or untrigger questions_b
          if (currentQuestion && currentQuestion.inputRule === 'input_rule_event') {
            // Find the indicator that contains this question
            const currentIndicator = currentSession.participant.indicators.find(indicator => 
              indicator.questions?.some(q => q.questionId === currentQuestion.questionId)
            )
            
            if (currentIndicator && currentIndicator.questions_b && currentIndicator.questions_b.length > 0) {
              // Check all input_rule_event questions in this indicator to see if ANY are answered "yes"
              const hasAnyYesAnswer = currentIndicator.questions?.some(q => {
                if (q.inputRule === 'input_rule_event') {
                  // Check the response for this question
                  const questionResponse = q.questionId === questionId 
                    ? response // Use the current response being saved
                    : currentSession.responses[q.questionId]; // Check stored responses
                  
                  return questionResponse?.responseScore === 1;
                }
                return false;
              }) || false;

              if (hasAnyYesAnswer) {
                // At least one trigger question is "yes" - add questions_b if not already added
                console.log('saveResponse: At least one input_rule_event is YES, adding questions_b from indicator');
                get().pushSecondaryIndicatorResponses();
              } else {
                // All trigger questions are "no" - remove questions_b
                console.log('saveResponse: All input_rule_event questions are NO, removing questions_b from indicator');
                get().removeSecondaryIndicatorResponses(currentIndicator.indicatorId);
              }
            }
          }
        },

        /**
         * Save a demographic response
         * These are background questions (age, gender, etc.)
         */
        saveDemographicResponse: (questionId: string, response: DemographicResponse) => {
          const { currentSession } = get()
          if (!currentSession) return
          
          set({
            currentSession: {
              ...currentSession,
              demographics: {
                ...currentSession.demographics,
                [questionId]: response
            },
              lastActivityAt: Date.now()
            }
          })
        },

        /**
         * Save a validity response
         * These are data quality check questions
         */
        saveValidityResponse: (questionId: string, response: ValidityResponse) => {
          const { currentSession } = get()
          if (!currentSession) return
          
          set({
            currentSession: {
              ...currentSession,
              validityResponses: {
                ...currentSession.validityResponses,
                [questionId]: response
              },
              lastActivityAt: Date.now()
            }
          })
        },

        /**
         * Set the current survey step
         * Used for navigation between survey sections
         */
        setCurrentStep: (step: SurveyStep) => {
          const { currentSession } = get()
          if (!currentSession) return
          
                set({
            currentSession: {
              ...currentSession,
              currentStep: step,
              lastActivityAt: Date.now()
            }
          })
        },

        /**
         * Set the current question ID
         * Used for navigation between questions within a step
         */
        setCurrentQuestion: (questionId: string | null) => {
          const { currentSession } = get()
          if (!currentSession) return
          
          set({
            currentSession: {
              ...currentSession,
              currentQuestionId: questionId,
              lastActivityAt: Date.now(),
              currentQuestionStartTime: Date.now() // Record when this question was displayed
            }
          })
        },

        /**
         * Set the survey language
         * Used for internationalization
         */
        setLanguage: (language: string) => {
          const { currentSession } = get()
          if (!currentSession) return
          
          set({
            currentSession: {
              ...currentSession,
              language,
              lastActivityAt: Date.now()
            }
          })
        },

        /**
         * Submit the completed survey to the backend
         * This is the final step that sends all collected data
         */
        submitSurvey: async () => {
          const { currentSession } = get()
          if (!currentSession) throw new Error('No active session')
          
          set({ isLoading: true, error: null })
          
          try {
            console.log('Submitting survey with responses:', currentSession.responses)
            console.log('Total responses count:', Object.keys(currentSession.responses).length)
            
            /**
             * BUILD SURVEY PAYLOAD
             * 
             * This section constructs the data structure expected by the backend:
             * 1. Group responses by indicator
             * 2. Include all participant metadata
             * 3. Separate regular responses, demographics, and validity responses
             */
            const indicators = currentSession.participant.indicators.map(indicator => {
              const indicatorResponses = Object.values(currentSession.responses)
                .filter(response => response.indicatorId === indicator.indicatorId)
              
              console.log(`Indicator ${indicator.indicatorId} (${indicator.indicator}):`, {
                expectedIndicatorId: indicator.indicatorId,
                responsesFound: indicatorResponses.length,
                responseIndicatorIds: Object.values(currentSession.responses).map(r => r.indicatorId)
              })
              
              return {
                indicator: indicator.indicator,
                contentId: indicator.contentId,
                indicatorId: indicator.indicatorId,
                tags: indicator.tags,
                responses: indicatorResponses.map(response => {
                  // Find the corresponding question to check for inputRule
                  const question = indicator.questions?.find(q => q.questionId === response.questionId) ||
                                   indicator.questions_b?.find(q => q.questionId === response.questionId)
                  
                  const responseData: {
                    questionId: string
                    questionText: string
                    responseOptionField: string
                    responseScore: number
                    questionResponseOptionsType: string
                    inputRule?: string
                    initialTimeSpentMs?: number
                    revisionTimeSpentMs?: number
                  } = {
                    questionId: response.questionId,
                    questionText: response.questionText,
                    responseOptionField: response.responseOptionField,
                    responseScore: response.responseScore,
                    questionResponseOptionsType: response.questionResponseOptionsType,
                  }
                  
                  // Include inputRule if it's "input_rule_event"
                  if (question?.inputRule === 'input_rule_event') {
                    responseData.inputRule = 'input_rule_event'
                  }
                  
                  // Include time tracking if available
                  if (response.initialTimeSpentMs !== undefined) {
                    responseData.initialTimeSpentMs = response.initialTimeSpentMs
                  }
                  if (response.revisionTimeSpentMs !== undefined) {
                    responseData.revisionTimeSpentMs = response.revisionTimeSpentMs
                  }
                  
                  return responseData
                })
              }
            })

            console.log('Final indicators structure:', indicators)

            const demographicQuestions = Object.values(currentSession.demographics)
            const validityQuestions = Object.values(currentSession.validityResponses).map((q: ValidityResponse) => ({
              questionId: q.questionId,
              ...(q.validityIndex !== undefined && { validityIndex: q.validityIndex }),
              responseOptionField: q.responseOptionField,
              responseScore: q.responseScore,
              ...(q.initialTimeSpentMs !== undefined && { initialTimeSpentMs: q.initialTimeSpentMs }),
              ...(q.revisionTimeSpentMs !== undefined && { revisionTimeSpentMs: q.revisionTimeSpentMs })
            }))

            const payload = {
              surveyId: currentSession.participant.surveyId,
              surveyParticipantId: currentSession.participant.surveyParticipantId,
              grade: currentSession.participant.grade,
              surveyStartedAt: currentSession.surveyStartedAt,
              surveySubmittedAt: Date.now(),
              surveyName: currentSession.participant.surveyName || "",
              indicators,
              createdAt: new Date().toISOString(),
              demographicQuestions,
              validityQuestions,
              isAllCore: currentSession.participant.isAllCore || false,
              orgId: currentSession.participant.orgId || '',
              dataSent: true,
              parentSurveyAuthCode: currentSession.participant.parentSurveyAuthCode,
              wellnessCenterCheck: currentSession.participant.wellnessCenterCheck,
              firstName: currentSession.participant.firstName,
              lastName: currentSession.participant.lastName,
              participantEmail: currentSession.participant.participantEmail,
              schoolName: currentSession.participant.schoolName,
              birthDate: currentSession.participant.birthDate,
              appliedTo: currentSession.participant.appliedTo,
            }

            console.log('Final payload being sent:', payload)

            await participantSurveyService.submitSurvey(payload)
            
            set({
              currentSession: {
                ...currentSession,
                isCompleted: true,
                currentStep: 'complete',
                lastActivityAt: Date.now()
              },
              isLoading: false
            })
          } catch (error) {
            console.log('[participant-survey-store][submitSurvey] Error:', error)
            set({
              error: (error as Error).message,
              isLoading: false
            })
            throw error
          }
        },

        /**
         * Push secondary questions_b from current indicator to main questions array
         * This is triggered when input_rule_event questions are answered with value 1
         * Matches the old project's conditional question logic
         */
        pushSecondaryIndicatorResponses: () => {
          const { currentSession } = get()
          if (!currentSession) return

          const currentQuestion = get().getCurrentQuestion()
          if (!currentQuestion) return

          // Find current indicator
          const currentIndicator = currentSession.participant.indicators?.find(
            ind => ind.indicatorId === currentQuestion.indicatorId
          )

          if (!currentIndicator || !currentIndicator.questions_b || currentIndicator.questions_b.length === 0) {
            console.log('pushSecondaryIndicatorResponses: No questions_b to add')
            return
          }

          console.log('pushSecondaryIndicatorResponses: Adding questions_b', {
            indicatorId: currentIndicator.indicatorId,
            questions_b_count: currentIndicator.questions_b.length,
            questions_b: currentIndicator.questions_b.map(q => ({ questionId: q.questionId, text: q.questionText }))
          });

          // Create new participant data with questions_b added to main questions array
          const updatedParticipant = {
            ...currentSession.participant,
            indicators: currentSession.participant.indicators.map(indicator => {
              if (indicator.indicatorId === currentIndicator.indicatorId) {
                // Filter out questions that have already been added (prevent duplicates)
                const questionsToAdd = currentIndicator.questions_b?.filter(qb => 
                  !indicator.questions?.find(q => q.questionId === qb.questionId)
                ) || []

                // Separate existing questions into regular questions and validity questions
                const existingQuestions = indicator.questions || []
                const regularQuestions = existingQuestions.filter(q => !q.isValidityQuestion)
                const validityQuestions = existingQuestions.filter(q => q.isValidityQuestion)
                
                return {
                  ...indicator,
                  questions: [
                    ...regularQuestions,
                    ...questionsToAdd,
                    ...validityQuestions // Keep validity questions at the end
                  ]
                }
              }
              return indicator
            })
          }

          set({
            currentSession: {
              ...currentSession,
              participant: updatedParticipant,
              lastActivityAt: Date.now()
            }
          })

          console.log('pushSecondaryIndicatorResponses: Successfully added questions_b to main questions array')
        },

        /**
         * Remove secondary questions_b from a specific indicator
         * This is called when all input_rule_event questions in an indicator are answered "no"
         */
        removeSecondaryIndicatorResponses: (indicatorId: string) => {
          const { currentSession } = get()
          if (!currentSession) return

          // Find the indicator with questions_b
          const targetIndicator = currentSession.participant.indicators?.find(
            ind => ind.indicatorId === indicatorId
          )

          if (!targetIndicator || !targetIndicator.questions_b || targetIndicator.questions_b.length === 0) {
            console.log('removeSecondaryIndicatorResponses: No questions_b to remove')
            return
          }

          console.log('removeSecondaryIndicatorResponses: Removing questions_b', {
            indicatorId: targetIndicator.indicatorId,
            questions_b_count: targetIndicator.questions_b.length
          });

          // Get the IDs of questions_b to remove
          const questionIdsToRemove = new Set(targetIndicator.questions_b.map(q => q.questionId))

          // Create new participant data with questions_b removed from main questions array
          const updatedParticipant = {
            ...currentSession.participant,
            indicators: currentSession.participant.indicators.map(indicator => {
              if (indicator.indicatorId === indicatorId) {
                // Filter out questions_b from the main questions array
                const filteredQuestions = indicator.questions?.filter(q => 
                  !questionIdsToRemove.has(q.questionId)
                ) || []

                return {
                  ...indicator,
                  questions: filteredQuestions
                }
              }
              return indicator
            })
          }

          // Also remove any responses to these questions
          const updatedResponses = { ...currentSession.responses }
          questionIdsToRemove.forEach(qId => {
            delete updatedResponses[qId]
          })

          set({
            currentSession: {
              ...currentSession,
              participant: updatedParticipant,
              responses: updatedResponses,
              lastActivityAt: Date.now()
            }
          })

          console.log('removeSecondaryIndicatorResponses: Successfully removed questions_b from main questions array')
        },

        /**
         * Clear the current session
         * Used when starting over or logging out
         */
        clearSession: () => {
          set(initialState)
        },

        /**
         * Reset error state
         * Used to clear error messages
         */
        resetError: () => {
          set({ error: null })
        },

        /**
         * Set loading state
         * Used for UI feedback during async operations
         */
        setLoading: (loading: boolean) => {
          set({ isLoading: loading })
        },

        /**
         * Get the current question object
         * Returns the question with indicator metadata attached
         */
        getCurrentQuestion: () => {
          const { currentSession } = get()
          if (!currentSession?.currentQuestionId) return null
          
          // DEBUG: Log when we're getting the problematic question
          if (currentSession.currentQuestionId === '5dd156f5-d424cc6c-673b32bc-55a7e503') {
            console.log('getCurrentQuestion: Returning problematic validity question', {
              currentQuestionId: currentSession.currentQuestionId,
              currentStep: currentSession.currentStep
            })
          }
          
          // Special handling for K3 surveys with introK3 questions
          const hasK3Indicators = currentSession.participant.indicators?.some(indicator => 
            indicator.appliesTo && indicator.appliesTo.includes('k3')
          );
          
          if (hasK3Indicators) {
            // Check introK3 questions first
            if (currentSession.participant.introK3) {
              const introQuestion = currentSession.participant.introK3.find((q: SurveyQuestion) => q.questionId === currentSession.currentQuestionId)
              if (introQuestion) {
                return {
                  ...introQuestion,
                  indicatorId: 'introK3',
                  indicator: 'Introduction',
                  contentId: 'introK3',
                }
              }
            }
          }
          
          // Check indicator questions (original logic)
          for (const indicator of currentSession.participant.indicators) {
            const question = indicator.questions?.find((q: SurveyQuestion) => q.questionId === currentSession.currentQuestionId)
            if (question) {
              // Return the question with indicator information
              return {
                ...question,
                indicatorId: indicator.indicatorId,
                indicator: indicator.indicator,
                contentId: indicator.contentId,
              }
            }
          }
          return null
        },

        /**
         * Get all questions in survey (unified method for all survey types)
         * Replaces duplicate logic across components
         */
        getAllQuestions: () => {
          const { currentSession } = get()
          if (!currentSession) return []

          const questions: SurveyQuestion[] = []
          
          // Special handling for K3 surveys with introK3 questions
          const hasK3Indicators = currentSession.participant.indicators?.some(indicator => 
            indicator.appliesTo && indicator.appliesTo.includes('k3')
          );
          
          if (hasK3Indicators && currentSession.participant.introK3) {
            questions.push(...currentSession.participant.introK3)
          }
          
          // Add indicator questions (includes dynamically added questions_b)
          for (const indicator of currentSession.participant.indicators) {
            if (indicator.questions && indicator.questions.length > 0) {
              questions.push(...indicator.questions)
            }
          }
          
          return questions
        },

        /**
         * Calculate survey progress with enhanced information
         * Returns ProgressInfo with consistent indexing
         */
        getProgress: () => {
          const { currentSession } = get()
          if (!currentSession || !currentSession.currentQuestionId) {
            return { 
              current: 0, 
              total: 0, 
              percentage: 0, 
              isLastQuestion: false, 
              currentIndex: -1 
            }
          }
          
          const allQuestions = get().getAllQuestions()
          const totalQuestions = allQuestions.length
          
          // Find current question position
          const currentIndex = allQuestions.findIndex(
            q => q.questionId === currentSession.currentQuestionId
          )
          
          if (currentIndex === -1) {
            return { 
              current: 0, 
              total: totalQuestions, 
              percentage: 0, 
              isLastQuestion: false, 
              currentIndex: -1 
            }
          }
          
          const currentPosition = currentIndex + 1 // Convert to 1-based
          const percentage = totalQuestions > 0 ? Math.round((currentPosition / totalQuestions) * 100) : 0
          const isLastQuestion = currentIndex === totalQuestions - 1
          
          return {
            current: currentPosition,
            total: totalQuestions,
            percentage,
            isLastQuestion,
            currentIndex
          }
        },

        /**
         * Get total number of responses
         * Includes both regular and validity responses
         */
        getResponsesCount: () => {
          const { currentSession } = get()
          if (!currentSession) return 0
          
          return Object.keys(currentSession.responses).length + Object.keys(currentSession.validityResponses).length
        },

        /**
         * Check if a question has been answered
         * Checks both regular and validity responses
         */
        isQuestionAnswered: (questionId: string) => {
          const { currentSession } = get()
          if (!currentSession) return false
          
          // Check both regular responses and validity responses
          return questionId in currentSession.responses || questionId in currentSession.validityResponses
        },

        /**
         * Check if user can proceed to next question
         * Ensures current question is answered before allowing navigation
         */
        canProceed: () => {
          const { currentSession } = get()
          if (!currentSession) return false
          
          const currentQuestion = get().getCurrentQuestion()
          if (!currentQuestion) return true
          
          // Check both regular responses and validity responses
          return currentQuestion.questionId in currentSession.responses || currentQuestion.questionId in currentSession.validityResponses
        },
      }),
      {
        name: 'participant-survey-store',
        partialize: (state) => ({
          currentSession: state.currentSession,
        }),
      }
    ),
    { name: 'participant-survey-store', enabled: import.meta.env.DEV }
  )
) 
